<template>
  <div>
    <Spinner v-if="!isInitialized" />
    <div class="report-container" v-if="isInitialized" :class="{ small: maxmimizedUI }">
      <div class="editor-container">
        <CoverImage v-if="maxmimizedUI" :image="image" @close="maxmimizedUI = false" />
        <Editor
          v-bind="content"
          :theme="pageTheme"
          :maxmimizedUI="maxmimizedUI"
          @editor:update="editorUpdate"
          @editor:resize="onResize"
        />
      </div>
      <div class="settings">
        <div class="top-row">
          <h4>{{ type }}</h4>
          <div class="d-flex">
            <v-btn
              @click="onReportPublish"
              text-color="white"
              :color="colors[report.article_status]"
              >Rapporten är {{ report.article_status === 'online' ? 'online' : 'offline' }}</v-btn
            >
            <v-btn
              elevation="2"
              color="primary"
              @click="save"
              :disabled="!hasUnsavedUpdates"
              :loading="isSaving"
              >Spara</v-btn
            >
            <CommonMenu
              :items="[
                { key: 'remove', value: 'Radera rapport / avhandling' },
                { key: 'markAsBubble', value: 'Visa som bubbla' },
              ]"
              @remove="onRemove"
              @markAsBubble="onMarkAsBubble"
            />
          </div>
        </div>
        <PreviewLink domain="havet" :string="report.article_title" />
        <div class="mt-3">
          <PlaceInBubble v-model="articleMeta" />
          <ReportStandard v-bind="report" @update="update" />
          <component
            :is="reportTypeComponent"
            v-bind="report"
            @update="update"
            @updateImmediately="saveImmediately"
          >
            <template #image><ReportBannerImage v-bind="report" @update="update" /></template>
            <template #keywords>
              <Keywords v-model="keywords" />
            </template>
          </component>
          <h4 class="mt-5">Taggar</h4>
          <v-chip-group v-model="reportTags" column multiple>
            <v-chip
              text-color=""
              color="primary"
              v-for="(tag, i) in allTags"
              :key="i"
              filter
              outlined
            >
              {{ tag.tag }}
            </v-chip>
          </v-chip-group>
        </div>
        <Message ref="message" />
        <Confirm ref="confirm" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Editor from '@/components/shared/editor/Editor'
import Spinner from '@/components/shared/Spinner'

import ReportStandard from '@/components/templates/ReportStandard'
import ReportAvhandling from '@/components/templates/ReportAvhandling'
import ReportLansstyrelse from '@/components/templates/ReportLansstyrelse'
import ReportOvrig from '@/components/templates/ReportOvrig'
import ReportMC from '@/components/templates/ReportMC'
import ReportBannerImage from '@/components/templates/ReportImage'
import Message from '@/components/shared/Message'
import Keywords from '@/components/shared/Keywords'
import { sections } from '@/assets/data/reportTypes'
import Confirm from '@/components/shared/Confirm'
import { colors } from '@/assets/data/colors'
import CoverImage from '@/components/shared/CoverImage'
import CommonMenu from '@/components/shared/CommonMenu'
import PreviewLink from '@/components/shared/PreviewLink'
import { defaultReportAvhandlingMetaData } from '@/assets/data/metaData.js'
import PlaceInBubble from '@/components/shared/PlaceInBubble'

export default {
  name: 'Report',
  props: {
    id: { type: [Number, String], required: true },
  },
  components: {
    Editor,
    Message,
    ReportStandard,
    ReportBannerImage,
    Keywords,
    Spinner,
    Confirm,
    CoverImage,
    CommonMenu,
    PreviewLink,
    PlaceInBubble,
  },
  data() {
    return {
      maxmimizedUI: false,
      colors,
      isInitialized: false,
      isSaving: false,
      updates: {},
      updatesContent: {},
    }
  },
  computed: {
    ...mapGetters('tags', { getAllTags: 'getArray' }),
    ...mapGetters('reports', { getReportById: 'getById' }),
    ...mapGetters('content', { getContent: 'getContentByParentId' }),

    hasUnsavedUpdates() {
      return !!Object.keys({ ...this.updates, ...this.updatesContent }).length
    },

    report() {
      return { ...this.getReportById(this.reportId), ...this.updates }
    },

    content() {
      return this.getContent(this.reportId)
    },
    reportId() {
      return Number(this.id)
    },

    image() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/870/${this.report.article_image_id}.jpg`
    },

    pageTheme() {
      return 'havet-theme'
    },
    keywords: {
      get() {
        return this.report.article_keywords
          ? this.report.article_keywords
              .replaceAll(',', ' ')
              .split(' ')
              .map((w) => w.trim())
              .filter((w) => w.length > 0)
              .join(' ')
          : ''
      },
      set(val) {
        this.update({ article_keywords: val })
      },
    },
    type() {
      return sections.find((s) => s.name === this.report.article_type).singelLabel
    },
    reportTypeComponent() {
      //   { id: 4, name: 'Havsutsikt', title: 'Havsutsiktsartiklar', label: 'havsutsiktsartiklarna' },
      //   { id: 5, name: 'svealandskusten', title: 'Svealandskusten', label: 'svealandsrapporterna' },
      switch (this.report.article_type) {
        case 'avhandling':
          return ReportAvhandling
        case 'Lans_rapp':
          return ReportLansstyrelse
        case 'orap':
          return ReportOvrig
        case 'MC-rapport':
          return ReportMC

        default:
          throw new Error('No valid report type')
      }
    },
    reportTags: {
      get() {
        if (!this.report.tags) return []
        return this.report.tags.map((t) => this.allTags.findIndex((at) => at.id === t.tag.id))
      },
      set(selectedTagsIndices) {
        const tags = selectedTagsIndices.map((i) => this.allTags[i]).map((t) => ({ tag: t }))
        this.update({ tags })
      },
    },

    allTags() {
      const allTags = this.getAllTags
      return allTags.sort((a, b) => a.group - b.group).sort((a, b) => a.tag.localeCompare(b.tag))
    },
    articleMeta: {
      get() {
        return this.report.article_meta
          ? JSON.parse(this.report.article_meta)
          : defaultReportAvhandlingMetaData
      },
      set(val) {
        this.saveImmediately({ article_meta: val })
      },
    },
  },
  methods: {
    ...mapActions('reports', {
      getSingleReport: 'read',
      updateReport: 'update',
      deleteReport: 'delete',
    }),
    ...mapActions('content', { getSingleContent: 'read', updateContent: 'update' }),
    ...mapActions('links', { createLink: 'create' }),

    async onRemove() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort rapport eller avhandling',
        message: 'Är du säker på att du vill ta bort rapporten/avhandlingen?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return

      await this.deleteReport({ ...this.getReportById(this.reportId) })
      this.$router.replace({ name: 'ReportsList' })
    },
    // async onCreateLink(_link) {
    //   const link = await this.createLink(_link)
    //   const payload = [...this.report.newsLinks, { link, nl_news_id: +this.id }] // nl_id = null because it doesnt exists
    //   this.saveImmediately({ newsLinks: payload })
    // },
    update(payload) {
      this.updates = { ...this.updates, ...payload }
    },

    onMarkAsBubble() {
      const payload = { article_meta: JSON.stringify({ ...this.articleMeta, bubble: true }) }
      this.saveImmediately(payload)
    },

    editorUpdate(payload) {
      this.updatesContent = payload
    },
    async saveImmediately(payload) {
      this.isSaving = true
      await this.updateReport({ ...this.report, ...payload })
      this.isSaving = false
    },
    async save() {
      try {
        if (!this.hasUnsavedUpdates) return
        this.isSaving = true
        await this.updateReport({ ...this.report, ...this.updates })
        if (Object.keys(this.updatesContent).length)
          await this.updateContent({ ...this.content, ...this.updatesContent })
        this.updates = {}
        this.updatesContent = {}
        this.$refs.message.open('Rapporten sparad')
      } catch (err) {
        throw new Error(err)
      } finally {
        this.isSaving = false
      }
    },
    async onReportPublish() {
      const newState = this.report.article_status === 'online' ? 'offline' : 'online'
      await this.saveImmediately({ article_status: newState })
    },
    onResize(val) {
      this.maxmimizedUI = val
    },
  },

  async created() {
    await this.getSingleReport({ id: this.id })
    await this.getSingleContent({ type: 'report', id: this.id })
    this.isInitialized = true
  },
  mounted() {
    this._keyListener = function (e) {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault() // present "Save Page" from getting triggered.
        this.save()
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener)
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedUpdates) {
      next()
      return
    }
    const shouldSave = await this.$refs.confirm.open({
      title: 'Osparade ändringar',
      message: 'Vill du spara innen du går vidare?',
      saveButtonText: 'Spara',
      cancelButtonText: 'Nej',
    })
    if (shouldSave) await this.save()

    next()
  },
}
</script>

<style lang="less" scoped>
.report-container {
  display: grid;
  grid-template-columns: auto 600px;
  gap: 15px;
  transition: all 0.3s;
  &.small {
    grid-template-columns: max(1100px);
    justify-content: center;
    & > div:nth-child(2) {
      visibility: hidden;
      display: none;
    }
  }
}
.settings {
  display: flex;
  flex-direction: column;
  gap: 5px;
  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
  }
}
</style>
